import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

/**
 * Inline Terms & Conditions summary shown on the /signup page so visitors
 * don't lose their place by navigating away to /terms. The full legal text
 * remains available at /terms via the footer.
 *
 * Colours aligned with the updated /signup page palette:
 *   bg  #EBF5F2  · ink  #1A2E2A  · accent  #2A7B6F  · body  gray-500
 */
export default function TermsDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-3xl max-w-md p-0 overflow-hidden max-h-[85vh] flex flex-col [&>button.absolute]:hidden border-0"
        style={{ background: "#fff" }}
        data-testid="signup-terms-dialog"
      >
        {/* Header */}
        <div
          className="relative px-6 sm:px-7 pt-6 pb-5 border-b shrink-0"
          style={{
            background: "linear-gradient(135deg, #EBF5F2 0%, #f5fbf9 100%)",
            borderColor: "#C2E0DA",
          }}
        >
          <h2
            className="text-xl sm:text-2xl font-black leading-snug pr-10 dark:text-[#1A2E2A]"
            style={{ color: "#1A2E2A" }}
          >
            Terms &amp; Conditions
          </h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center border transition"
            style={{
              color: "#2A7B6F",
              background: "rgba(255,255,255,0.8)",
              borderColor: "#C2E0DA",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#2A7B6F";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.8)";
              e.currentTarget.style.color = "#2A7B6F";
            }}
            data-testid="signup-terms-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div
          className="overflow-y-auto px-6 sm:px-7 py-5 space-y-3.5 text-[12.5px] leading-relaxed flex-1 min-h-0"
          style={{ color: "#4A5568" }}
          data-testid="signup-terms-body"
        >
          <p>
            By creating a Kiddo+ account you agree to use the platform only for personal,
            non-commercial purposes — to understand and support your child&apos;s development.
          </p>
          <p>
            Kiddo+ provides educational, evidence-informed guidance from occupational therapists.
            It does not diagnose conditions and is not a replacement for medical or therapy services.
            Always consult a qualified professional for clinical concerns.
          </p>
          <p>
            We collect what&apos;s needed to personalise activities and reports (child profile,
            assessment answers, activity logs). We never sell your data. You can delete your account
            and all related data at any time from <em>Settings → Account</em>.
          </p>
          <p>
            Free features stay free. Pro is opt-in, billed via Razorpay, and you can cancel
            anytime — your remaining paid period stays accessible until renewal.
          </p>
          <p>
            <strong style={{ color: "#1A2E2A" }}>Accurate information.</strong> Please use real
            details about your child&apos;s age and stage so the activities and reports stay
            meaningful. Misrepresenting age can give unsafe suggestions.
          </p>
          <p>
            <strong style={{ color: "#1A2E2A" }}>Be kind.</strong> Don&apos;t share other
            families&apos; content publicly, scrape resources, or attempt to access another
            parent&apos;s account. Abuse or scraping leads to immediate suspension.
          </p>
          <p>
            <strong style={{ color: "#1A2E2A" }}>Updates.</strong> We may improve features and
            content; we&apos;ll email significant changes. Continued use after an update means
            you accept the updated terms.
          </p>
          <p className="text-[11.5px] mt-2 italic" style={{ color: "#6B7280" }}>
            Read the full legal text at{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2A7B6F" }}
              className="hover:underline"
            >
              /terms
            </a>
            .
          </p>
        </div>

        {/* Footer */}
        <div
          className="shrink-0 border-t px-6 sm:px-7 py-4"
          style={{ background: "rgba(235,245,242,0.6)", borderColor: "#C2E0DA" }}
        >
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full text-white rounded-full font-bold py-5 border-0"
            style={{ background: "#2A7B6F" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#1f6459"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#2A7B6F"; }}
            data-testid="signup-terms-understand"
          >
            I understand
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
